import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

function VerificationPendingPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pollingInterval, setPollingInterval] = useState(null);

  useEffect(() => {
    // If user becomes verified, redirect to home
    if (user?.isVerified) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusInfo = () => {
    const status = user?.verificationStatus || 'not_submitted';
    
    switch(status) {
      case 'pending':
        return {
          icon: <Clock className="w-16 h-16 text-yellow-500" />,
          title: 'Verification Under Review',
          message: 'Your identity verification documents are being reviewed by our team.',
          detail: 'This process typically takes 24-48 hours. We will notify you via email once your account is approved.',
          color: 'yellow'
        };
      case 'verified':
        return {
          icon: <CheckCircle className="w-16 h-16 text-green-500" />,
          title: 'Verification Complete!',
          message: 'Your account has been verified successfully.',
          detail: 'You now have full access to all features. Redirecting...',
          color: 'green'
        };
      case 'rejected':
        return {
          icon: <XCircle className="w-16 h-16 text-mint-500" />,
          title: 'Verification Unsuccessful',
          message: 'Unfortunately, we were unable to verify your identity with the documents provided.',
          detail: 'Please contact support for more information or to resubmit your documents.',
          color: 'red'
        };
      default:
        return {
          icon: <AlertCircle className="w-16 h-16 text-blue-500" />,
          title: 'Profile Submission Required',
          message: 'Please complete your profile and submit verification documents.',
          detail: 'You need to submit your profile information and identity documents to access the platform.',
          color: 'blue'
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Main Status Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            {statusInfo.icon}
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {statusInfo.title}
          </h1>

          {/* Message */}
          <p className="text-lg text-gray-700 mb-4">
            {statusInfo.message}
          </p>

          {/* Detail */}
          <p className="text-base text-gray-600 mb-8">
            {statusInfo.detail}
          </p>

          {/* Status Badge */}
          <div className="inline-flex items-center space-x-2 bg-gray-100 rounded-full px-6 py-3 mb-8">
            <div className={`w-3 h-3 rounded-full ${
              statusInfo.color === 'yellow' ? 'bg-yellow-500 animate-pulse' :
              statusInfo.color === 'green' ? 'bg-green-500' :
              statusInfo.color === 'red' ? 'bg-cream-1000' :
              'bg-blue-500'
            }`}></div>
            <span className="text-sm font-semibold text-gray-700">
              Status: {user?.verificationStatus?.replace('_', ' ').toUpperCase() || 'NOT SUBMITTED'}
            </span>
          </div>

          {/* Timeline */}
          {user?.verificationStatus === 'pending' && (
            <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-gray-900 mb-4">What happens next?</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Documents Submitted</p>
                    <p className="text-sm text-gray-600">Your profile and ID documents have been received</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center flex-shrink-0 mt-0.5 animate-pulse">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Under Review</p>
                    <p className="text-sm text-gray-600">Our team is verifying your identity and documents</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs text-gray-600">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-500">Phone Verification</p>
                    <p className="text-sm text-gray-500">We will call you to verify your identity</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs text-gray-600">4</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-500">Approval & Access</p>
                    <p className="text-sm text-gray-500">Full platform access once approved</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rejected - Resubmit Option */}
          {user?.verificationStatus === 'rejected' && (
            <>
              <div className="bg-cream-100 border border-red-200 rounded-xl p-6 mb-6 text-left">
                <h3 className="font-semibold text-red-900 mb-2">Rejection Reason:</h3>
                <p className="text-sm text-red-800 mb-4">
                  {user?.rejectionReason || 'Identity verification failed'}
                </p>
                <p className="text-sm text-red-700">
                  Please review the reason above and resubmit your verification with correct documents.
                </p>
              </div>
              
              <button
                onClick={() => navigate('/complete-profile')}
                className="bg-mint-500 hover:bg-mint-600 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 mb-4"
              >
                Resubmit Verification
              </button>
              
              <p className="text-sm text-gray-600 mb-6">
                Need help? Contact us at <a href="mailto:quboolme@gmail.com" className="underline font-semibold text-navy-500">quboolme@gmail.com</a>
              </p>
            </>
          )}

          {/* Not Submitted - Redirect to Complete Profile */}
          {user?.verificationStatus === 'not_submitted' && (
            <button
              onClick={() => navigate('/complete-profile')}
              className="bg-mint-500 hover:bg-mint-600 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 mb-4"
            >
              Complete Profile Now
            </button>
          )}

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="flex items-center justify-center space-x-2 mx-auto text-gray-600 hover:text-gray-900 transition-colors duration-200"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>

        {/* Footer Note */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Questions? Email us at <a href="mailto:quboolme@gmail.com" className="text-navy-500 hover:underline">quboolme@gmail.com</a></p>
        </div>
      </div>
    </div>
  );
}

export default VerificationPendingPage;